import { IxError } from '@sdk/errors';
import { Network, PaymentMethod, Token } from '@sdk/shared/types/config.types';
import {
  CreateQuickTradePayload,
  OrderStateKey,
  QuickTrade,
  QuickTradeStatus,
} from '@sdk/shared/types/order.types';
import { Currency } from '@sdk/shared/types/config.types';
import { BackendAccount } from '@sdk/shared/types/settings.types';
import { ExtendedWalletClient } from '@sdk/auth/types';

export interface ParsedChatJson {
  id: number;
  text: string;
  attachments: any[];
  sender: string;
  receiver: string;
  orderId: string;
  createdAt: string;
  status?: string;
}

export type RawNotification = {
  id?: string;
  data?: string | { json?: string };
  messageId?: string;
  addressId?: string;
  title?: string;
  body?: string;
  notification?: { title?: string; body?: string };
  red?: boolean;
};

export type ParsedOrderJson = {
  id: string;
  from: string;
  messageId?: string;
  addressId?: string;
  title?: string;
  body?: string;
  note: 'order' | 'chat';
  orderHash: string;
  state: OrderStateKey;
  red: boolean;
  createdAt: Date;
  quickTradeId?: string;
};
export type ParseNotificationResult = ParsedChatJson | ParsedOrderJson | QuickTradeNotificationData;

export interface QuickTradeNotificationData {
  quickTrade?: CreateQuickTradePayload & Partial<QuickTrade>;
  adId?: number;
  quickTradeId?: string;
  orderHash?: string;
  state?: QuickTradeStatus['status'] | 'intent';
  rejectedAdMerchant?: string;
}

export enum IxEventType {
  CHAT = 'chat',
  ORDER = 'order',
  QUICK_TRADE = 'quickTrade',
  ERROR = 'error',
  WALLET_CONNECTED = 'walletConnected',
  WALLET_DISCONNECTED = 'walletDisconnected',
  UNSUPPORTED_NETWORK = 'unsupportedNetwork',
  DEBUG = 'debug',
  INFO = 'info',
  INITIALIZED = 'initialized',
  AUTHENTICATED = 'authenticated',
  SIWE_AUTHENTICATING = 'siweAuthenticating',
  WEBSOCKET_CONNECTED = 'websocketConnected',
  WEBSOCKET_DISCONNECTED = 'websocketDisconnected',
  CHAIN_SWITCHED = 'chainSwitched',
  SIGN_JSON_RPC_REQUEST = 'signJSONRPCRequest',
}

export interface BaseEvent {
  type: IxEventType;
  data: Record<string, any>;
}

export type IxEventData =
  | ParsedChatJson
  | ParsedOrderJson
  | QuickTradeNotificationData
  | ChainEventData;

export interface OrderEvent extends BaseEvent {
  type: IxEventType.ORDER;
  data: ParsedOrderJson;
}

export interface QuickTradeEvent extends BaseEvent {
  type: IxEventType.QUICK_TRADE;
  data: QuickTradeNotificationData;
}

export interface SiweAuthenticatingEvent extends BaseEvent {
  type: IxEventType.SIWE_AUTHENTICATING;
  data: ChainEventData;
}

export interface AuthenticatedEvent extends BaseEvent {
  type: IxEventType.AUTHENTICATED;
  data: AuthenticatedEventData;
}

export interface ChatEvent extends BaseEvent {
  type: IxEventType.CHAT;
  data: ParsedChatJson;
}

export interface ChainSwitchedEvent extends BaseEvent {
  type: IxEventType.CHAIN_SWITCHED;
  data: ChainEventData;
}

export interface SignJSONRPCRequestEvent extends BaseEvent {
  type: IxEventType.SIGN_JSON_RPC_REQUEST;
  data: {
    message: string;
  };
}

export interface ChainEventData {
  previousChainId?: number | null;
  currentChainId: number;
  address?: string;
}
export interface AuthenticatedEventData {
  chainId: number | null;
  account: BackendAccount | null;
  walletClient: ExtendedWalletClient;
}
export interface InitializedEventData {
  networks: Network[];
  currencies: Currency[];
  paymentMethods: PaymentMethod[];
  tokens: Token[];
  quickTradeWaitTime: number;
}

export interface UnsupportedNetworkEvent extends BaseEvent {
  type: IxEventType.UNSUPPORTED_NETWORK;
  data: ChainEventData;
}

export interface ErrorEvent extends BaseEvent {
  type: IxEventType.ERROR;
  data: NonNullable<IxError['data']>;
}

export interface InitializedEvent extends BaseEvent {
  type: IxEventType.INITIALIZED;
  data: InitializedEventData;
}

export interface WalletConnectedEvent extends BaseEvent {
  type: IxEventType.WALLET_CONNECTED;
  data: ChainEventData;
}

export interface WalletDisconnectedEvent extends BaseEvent {
  type: IxEventType.WALLET_DISCONNECTED;
  data: ChainEventData;
}

export interface WebSocketConnectedEvent extends BaseEvent {
  type: IxEventType.WEBSOCKET_CONNECTED;
  data: {
    isConnected: true;
    channelName: string;
  };
}

export interface WebSocketDisconnectedEvent extends BaseEvent {
  type: IxEventType.WEBSOCKET_DISCONNECTED;
  data: {
    isConnected: false;
    channelName: string;
  };
}

export type IxEvent = IxEvents[keyof IxEvents];

// -------------------------------------------
// Generic mapping from event "type" → payload
// -------------------------------------------

// 1. Union of every concrete event interface
export type IxEventUnion =
  | ChainSwitchedEvent
  | WalletConnectedEvent
  | WalletDisconnectedEvent
  | WebSocketConnectedEvent
  | WebSocketDisconnectedEvent
  | SiweAuthenticatingEvent
  | AuthenticatedEvent
  | InitializedEvent
  | ChatEvent
  | OrderEvent
  | QuickTradeEvent
  | ErrorEvent
  | BaseEvent
  | UnsupportedNetworkEvent
  | SignJSONRPCRequestEvent;

// 2. Helper – pick the event with matching `type`
type EventOf<T extends IxEventType> = Extract<IxEventUnion, { type: T }>;

// 3. Mapped type: enum value → event payload
export type IxEvents = {
  [K in IxEventType]: EventOf<K>;
};
